import { useCallback, useEffect, useState } from 'react';
import { Button } from '../../_core/button/button.component';
import { ISectionData } from './section.types';
import { useConfirm } from '../../_core/confirm/confirm.utils';
import { SaveProps } from '../_core/types';
import { requestPollingPausedVar } from '../../domains/request/request.service';
import { checkIfCanComplete } from './section.utils';
import { BlockType } from '../_core/editor.const';
import { IBranchData } from '../branch';
import { IRepeatSectionData } from '../repeat-section';
import toast from 'react-hot-toast';

interface ISectionButtonsProps {
  canComplete: boolean;
  completed: boolean;
  getData: (_?: SaveProps) => Promise<ISectionData | null>;
  prevBlock: number | null;
  nextBlock: number | null;
  navigateToBlock?: (_: number) => void;
  onNavigateToSection?: (_: number) => void;
  readOnly?: boolean;
  toggleCompleted: (_: boolean, timestamp?: Date) => void;
  uid?: string;
}

export const SectionButtons = ({
  canComplete,
  getData,
  prevBlock,
  nextBlock,
  navigateToBlock,
  onNavigateToSection,
  readOnly,
  toggleCompleted,
  uid,
  ...rest
}: ISectionButtonsProps) => {
  const [completed, setCompleted] = useState(rest.completed);
  const [completing, setCompleting] = useState(false);

  useEffect(() => setCompleted(rest.completed), [rest.completed]);

  const { ConfirmationDialog, confirm } = useConfirm({ title: 'Are you sure everything is complete?' });

  // TODO: IS this being used?
  const countUncompletedSectionFields = useCallback((data: ISectionData) => {
    let uncompletedOptional = 0;
    let uncompletedRequired = 0;

    for (const block of data.outputData.blocks) {
      const isBlockUnfilled = 'filled' in block.data && !block.data.filled;
      const isBlockRequired = 'required' in block.data && block.data.required;

      // Determine filled status of nested section blocks
      if (block.type === BlockType.QuestionGroup) {
        const uncompletedQuestionGroup = countUncompletedSectionFields(block.data);
        uncompletedOptional += uncompletedQuestionGroup.uncompletedOptional;
        uncompletedRequired += uncompletedQuestionGroup.uncompletedRequired;
      }
      // Branch and Multi Question Group should be considered as 1 item if triggering questions are not answered, and otherwise count their nested section data
      else if (block.type === BlockType.BranchYesNo || block.type === BlockType.BranchMulti) {
        const branchData = block.data as IBranchData;
        if (isBlockUnfilled || !branchData.questionData.value) {
          if (isBlockRequired) uncompletedRequired += 1;
          else uncompletedOptional += 1;
        } else {
          if (branchData.questionData.value) {
            const branchSelectedSection =
              branchData.sectionsData[
                branchData.questionData.options.findIndex((o) => o === branchData.questionData.value)
              ];

            const uncompletedBranchSection = countUncompletedSectionFields(branchSelectedSection);
            uncompletedOptional += uncompletedBranchSection.uncompletedOptional;
            uncompletedRequired += uncompletedBranchSection.uncompletedRequired;
          } else toast.error('Failed to determine completion status of branch block.');
        }
      } else if (block.type === BlockType.RepeatSection) {
        if (isBlockUnfilled) {
          if (isBlockRequired) uncompletedRequired += 1;
          else uncompletedOptional += 1;
        } else {
          const repeatSectionData = block.data as IRepeatSectionData;
          if (repeatSectionData.repeatCount) {
            for (let i = 0; i < repeatSectionData.repeatCount; i++) {
              const sectionData = repeatSectionData.sectionsData[i];
              const uncompletedRepeatSection = countUncompletedSectionFields(sectionData);
              uncompletedOptional += uncompletedRepeatSection.uncompletedOptional;
              uncompletedRequired += uncompletedRepeatSection.uncompletedRequired;
            }
          } else toast.error('Failed to determine completion status of Question Group - Multi block.');
        }
      } else {
        if (isBlockUnfilled) {
          if (isBlockRequired) uncompletedRequired += 1;
          else uncompletedOptional += 1;
        }
      }
    }

    return { uncompletedOptional, uncompletedRequired };
  }, []);

  const onToggleComplete = useCallback(() => {
    setCompleting(true);
    getData({ forceFetchLatestData: true })
      .then(async (latestData) => {
        if (!latestData) return null;

        const timestamp = latestData.updatedTimestamp;

        if (completed) {
          toggleCompleted(false, timestamp);
          setCompleted(false);
        } else {
          if (await checkIfCanComplete({ confirm, data: latestData, uid })) {
            toggleCompleted(true, timestamp);
            setCompleted(true);
            // if (nextBlock) navigateToBlock(nextBlock);
          }
        }

        return;
      })
      .finally(() => {
        setCompleting(false);
        requestPollingPausedVar(false);
      });
  }, [completed, confirm, getData, toggleCompleted, uid]);

  const handleNavigate = useCallback(
    (targetBlock: number) => {
      // If in readonly single-section mode, use section navigation (like TOC)
      if (readOnly && onNavigateToSection) {
        onNavigateToSection(targetBlock);
      }
      // Otherwise, use the existing block navigation (collapse/expand)
      else if (navigateToBlock) {
        navigateToBlock(targetBlock);
      }
    },
    [readOnly, onNavigateToSection, navigateToBlock]
  );

  return (
    <>
      <div className="w-1/2 flex justify-around mx-auto py-2 print:hidden">
        {prevBlock !== null ? (
          <Button text="Prev" variant="secondary" size="medium" onClick={() => handleNavigate(prevBlock)} />
        ) : (
          <div className="w-28" />
        )}
        {canComplete ? (
          <Button
            text={completed ? 'Completed' : 'Mark Section Completed'}
            variant={completed ? 'success' : 'primary'}
            size="xl"
            onClick={onToggleComplete}
            loading={completing}
          />
        ) : (
          <div className="w-52" />
        )}
        {nextBlock !== null ? (
          <Button text="Next" variant="secondary" size="medium" onClick={() => handleNavigate(nextBlock)} />
        ) : (
          <div className="w-28" />
        )}
      </div>
      <ConfirmationDialog />
    </>
  );
};
